import { TEST_ENVS } from './constants';
import type { FlowEnvironment, TestEnv } from './types';

if (typeof window === 'undefined') {
  throw new Error('This module can only be used in a browser environment.');
}

/**
 * Extends the global Window interface to include custom properties used in the FLOW client environment.
 *
 * @property {FlowEnvironment} FLOW_CLIENT_ENVIRONMENT
 *   Specifies the current environment in which the FLOW client is running.
 *
 * @property {string} FLOW_CLIENT_BUILD
 *   Represents the build identifier or version of the FLOW client.
 *
 * @property {string} ASSETS_DOMAIN
 *   The domain from which static assets are served.
 */
declare global {
  interface Window {
    FLOW_CLIENT_ENVIRONMENT: FlowEnvironment;
    FLOW_CLIENT_BUILD: string;
    ASSETS_DOMAIN: string;
  }
}

const getEnvironment = (): FlowEnvironment => window.FLOW_CLIENT_ENVIRONMENT;
const getCurrentVersion = (): string => window.FLOW_CLIENT_BUILD || 'dev';

const isEnvironment = (env: FlowEnvironment): boolean => getEnvironment() === env;

const isDevEnv = () => isEnvironment('development');
const isStagingEnv = () => isEnvironment('staging');
const isProductionEnv = () => isEnvironment('production');
const isTestEnv = (): boolean => TEST_ENVS.includes(getEnvironment() as TestEnv);
const isPublicTestEnv = () => isEnvironment('public-test');

export {
  isEnvironment,
  getCurrentVersion,
  isDevEnv,
  isStagingEnv,
  isTestEnv,
  isPublicTestEnv,
  isProductionEnv,
};

export default getEnvironment;
